import {Avatar, Box, Typography, useTheme} from '@mui/material';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {CampaignOverview} from '../../types';
import {getCampaignStatus, getCampaignTimeLeft} from '../../utils/campaign';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {Colors} from '../common/colors';
import Indicator from './indicator';
import TagElement from './tag';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {Multiline} from '../common/text';
import Link from 'next/link';
import {getLocaleLabels} from '../../utils/lang';
import Image from 'next/image';
import {RATIO_IMAGE} from '../../utils/settings';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';

type CampaignOverviewElementProps = {
    campaign: CampaignOverview;
};

export default function CampaignOverviewElement(props: CampaignOverviewElementProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode == 'dark';
    const {t, i18n} = useTranslation('common');
    const router = useRouter();

    // console.log('CAMPAIGN = ', data);
    if (!props.campaign) {
        return <></>;
    }

    const campaign = props.campaign;
    const labels = getLocaleLabels(campaign.labels, i18n.language);
    const picture = labels.cover_url;
    const percentage = Math.round(
        campaign.goal > 0 ? (campaign.amount / campaign.goal) * 100 : 0
    );

    const authorPicture = campaign.author.picture_url;

    const status = getCampaignStatus(campaign);
    // console.log('STATUS = ', status);

    const timeLeft = getCampaignTimeLeft(campaign);
    // console.log('timeLeft = ', timeLeft);

    return (
        <Link
            href={'/' + campaign.url}
            style={{
                textDecoration: 'none',
                color: isDark ? 'white' : 'black',
            }}
        >
            <Box
                sx={{
                    cursor: 'pointer',
                    width: 300,
                    boxShadow: 3,
                    borderRadius: '8px',
                    '&:hover': {boxShadow: 12},
                    margin: '15px',
                }}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
            >
                {/* <Typography>{t(status)}</Typography> */}
                <Box display="flex" flexDirection="row" justifyContent="center">
                    {Boolean(picture) && (
                        <Image
                            src={picture}
                            alt={labels.title}
                            width={300}
                            height={Math.floor(300 / RATIO_IMAGE)}
                            style={{
                                borderTopLeftRadius: '8px',
                                borderTopRightRadius: '8px',
                            }}
                        />
                    )}
                    {!Boolean(picture) && (
                        <HideImageOutlinedIcon
                            sx={{
                                width: 300,
                                height: Math.floor(300 / RATIO_IMAGE),
                                margin: 'auto',
                                borderTopLeftRadius: '8px',
                                borderTopRightRadius: '8px',
                                display: 'block',
                            }}
                        />
                    )}
                </Box>
                <Box
                    padding={'5px'}
                    sx={{
                        border: '1px solid #ccc',
                        borderBottomLeftRadius: '5px',
                        borderBottomRightRadius: '5px',
                        borderTop: '0px',
                    }}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        marginTop={'10px'}
                        alignItems="center"
                    >
                        <Box display="flex" flexDirection="row">
                            <Avatar
                                alt={campaign.author.display_name}
                                src={authorPicture}
                                imgProps={{referrerPolicy: 'no-referrer'}}
                                sx={{width: 32, height: 32}}
                            />
                            <Typography
                                alignSelf={'center'}
                                marginLeft={'5px'}
                                sx={{
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    maxWidth: '250px',
                                }}
                            >
                                {campaign.author.display_name}
                            </Typography>
                        </Box>
                        <Box display="flex" flexDirection="row" alignSelf="center">
                            <LocalOfferIcon sx={{marginRight: '5px', fontSize: '32px'}} />
                            {campaign.tags.map((t) => (
                                <TagElement
                                    tag={t.tag}
                                    key={t.tag.tag_id}
                                    sx={{maxWidth: '250px'}}
                                />
                            ))}
                        </Box>
                    </Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            marginTop: '10px',
                            marginBottom: '10px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            height: '48px',
                        }}
                    >
                        {labels.title}
                    </Typography>
                    <Multiline
                        numberOfLine={2}
                        typo={{sx: {textAlign: 'justify', marginTop: '10px'}}}
                    >
                        <>{labels.subtitle}</>
                    </Multiline>
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-evenly"
                        marginTop={'10px'}
                        marginBottom={'10px'}
                    >
                        <Indicator
                            text={campaign.contribution.toString()}
                            icon={
                                <GroupsIcon sx={{alignSelf: 'center', color: Colors.first}} />
                            }
                        />
                        <Indicator
                            text={timeLeft.amount.toString() + ' ' + t(timeLeft.short)}
                            icon={
                                <CalendarMonthIcon
                                    sx={{alignSelf: 'center', color: Colors.first}}
                                />
                            }
                        />
                    </Box>
                </Box>
            </Box>
        </Link>
    );
}
